// src/pages/BankWalaHome.jsx
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

const faqData = {
  "Bank Employees (All Roles)": [
    // Negative
    {
      sentiment: "negative",
      question: "I barely get time between customers, targets, and branch tasks. How will BankWala help me?",
      answer:
        "BankWala gives you short, actionable tools—2-minute learnings, micro-assessments, instant productivity boosters, and auto-organized tasks. You don’t need long hours; you just need small consistent steps.",
    },
    {
      sentiment: "negative",
      question: "Is this another learning app with long boring modules?",
      answer:
        "No. BankWala converts banking knowledge into crisp, bite-sized content—explainers, audio notes, checklists, case-based mini-lessons, and ready templates — practical, not theoretical.",
    },
    {
      sentiment: "negative",
      question: "I am not very tech-savvy. Will I be able to use it?",
      answer:
        "BankWala is simple: large icons, clean menus, guided steps, and minimal inputs. Anyone—from teller to BM—can use it comfortably.",
    },
    {
      sentiment: "negative",
      question: "Will this add more tasks on top of my workload?",
      answer:
        "Not at all. BankWala reduces confusion, automates follow-ups, organizes your day, and eliminates rework. It *saves* time, it doesn’t add to it.",
    },

    // Neutral
    {
      sentiment: "neutral",
      question: "What can I do every day on BankWala?",
      answer:
        "You can complete micro-learnings, check daily tasks, track goals, update your diary, improve your score, watch banking explainers, and check compliance reminders.",
    },
    {
      sentiment: "neutral",
      question: "Does BankWala update content regularly?",
      answer:
        "Yes. Daily banking insights, new case studies, product explainers, compliance alerts, templates, and productivity boosters are refreshed continuously.",
    },
    {
      sentiment: "neutral",
      question: "Is BankWala free?",
      answer:
        "Core learning and productivity tools are free. Some advanced modules, workshops, and premium tracks may have optional charges.",
    },

    // Positive
    {
      sentiment: "positive",
      question: "Can BankWala help me become more efficient in my role?",
      answer:
        "Absolutely. With TRACK, Kanban, DeepThinking, AI Counselling, and the Goal Board — you get clarity, speed, discipline, and consistency.",
    },
    {
      sentiment: "positive",
      question: "Will this help me grow in my banking career?",
      answer:
        "Yes. Continuous learning, skill tracking, performance habits, and personalized growth plans make you promotion-ready with real improvement.",
    },
    {
      sentiment: "positive",
      question: "Can I improve my daily discipline in just a few minutes?",
      answer:
        "Yes. Small daily steps—micro-learning, reflection, goal checks, task tracking—compound into large professional growth over months.",
    },
  ],

  // -----------------------------------------
  "Branch Managers (BM / ABM)": [
    // Negative
    {
      sentiment: "negative",
      question: "My team is inconsistent. Can BankWala help them stay accountable?",
      answer:
        "Yes. With daily tasks, dashboards, nudges, self-scores, and habit tracking—your team becomes more responsible and self-driven.",
    },

    // Neutral
    {
      sentiment: "neutral",
      question: "Can I use BankWala to guide and train my staff?",
      answer:
        "Yes. You get easy modules, explainers, ready training content, case studies, and trackable learning — enabling team development daily.",
    },

    // Positive
    {
      sentiment: "positive",
      question: "Will this help me improve branch performance?",
      answer:
        "Definitely. Productivity tools, goal board, DeepThinking Lab, and continuous skill upgrades directly improve conversions, audits, service, and discipline.",
    },
  ],

  "New Joiners / Freshers": [
    {
      sentiment: "neutral",
      question: "I am new to banking. Can BankWala help me learn faster?",
      answer:
        "Yes. You get structured basics, simplified lessons, product explainers, case-based scenarios, and daily guidance — all in beginner-friendly language.",
    },
    {
      sentiment: "positive",
      question: "Will BankWala help me feel confident at work?",
      answer:
        "Yes. With daily micro-learning, self-checks, templates, and AI Counselling, your confidence grows naturally with practice.",
    },
  ],

  "Operations, Teller & Service Roles": [
    {
      sentiment: "neutral",
      question: "Can BankWala help reduce mistakes in daily operations?",
      answer:
        "Yes. With checklists, compliance reminders, process explainers, and quick learning modules, your accuracy improves and errors reduce.",
    },
    {
      sentiment: "positive",
      question: "Will this improve my customer-handling skills?",
      answer:
        "Absolutely. You get real examples, ready scripts, objection handling sheets, and soft-skill boosters.",
    },
  ],

  "Sales Teams (CASA, Loans, Insurance, Assets)": [
    {
      sentiment: "neutral",
      question: "Can BankWala help me improve conversions?",
      answer:
        "Yes. You get pitch scripts, objection handling flows, daily learning, goal tracking, lead Kanban, and productivity boosts.",
    },
    {
      sentiment: "positive",
      question: "Will BankWala help me stay consistent?",
      answer:
        "Yes. Your daily targets, reminders, nudges, and tracking routines build discipline step by step.",
    },
  ],

  "Regional & Cluster Heads": [
    {
      sentiment: "neutral",
      question: "Can this help improve team readiness and discipline?",
      answer:
        "Yes. BankWala builds a culture of daily learning, action discipline, and self-accountability across branches.",
    },
    {
      sentiment: "positive",
      question: "Will this reduce skill gaps across branches?",
      answer:
        "Absolutely. Continuous banking learning ensures everyone grows uniformly — from teller to BM.",
    },
  ],

  "Bank Marketplace Partners": [
    {
      sentiment: "neutral",
      question: "What offerings can we integrate on BankWala?",
      answer:
        "Training services, banking tools, books, productivity products, learning resources, and support services relevant to bankers.",
    },
    {
      sentiment: "positive",
      question: "Will BankWala improve customer trust for partners?",
      answer:
        "Yes. With reviews, credibility checks, curated listings, and transparent engagement, trust increases naturally.",
    },
  ],

  "Content Creators (bankTube)": [
    {
      sentiment: "neutral",
      question: "What content performs best on bankTube?",
      answer:
        "Product explainers, audit tips, soft-skills content, customer stories, case studies, learning shorts, and compliance hacks perform best.",
    },
    {
      sentiment: "positive",
      question: "Can creators earn through BankWala?",
      answer:
        "Yes. Through Rewarding Ads, ValueShare, micro-earning modules, and creator bonus pools.",
    },
  ],
  "BankWala AI": [
    {
      sentiment: "neutral",
      question: "What can BankWala AI help me with?",
      answer:
        "It can explain banking concepts, solve doubts, draft scripts, help write emails, prepare pitches, guide learning, and improve task clarity.",
    },
    {
      sentiment: "positive",
      question: "Does BankWala AI actually help me grow?",
      answer:
        "Yes. It learns from your role, goals, learning history, and daily actions — giving you personalized support and actionable guidance.",
    },
  ],
};

const BankWalaFAQHome = () => {
  const [openSections, setOpenSections] = useState({}); 

  const toggleSection = (sectionKey, itemIndex) => {
    setOpenSections((prev) => ({
      ...prev,
      [`${sectionKey}-${itemIndex}`]: !prev[`${sectionKey}-${itemIndex}`],
    }));
  };

  const getSentimentClasses = (sentiment) => {
    switch (sentiment) {
      case "negative":
        return "bg-red-950/20 dark:bg-red-900/20 border-red-900/30 dark:border-red-800/30";
      case "positive":
        return "bg-orange-950/20 dark:bg-orange-900/20 border-orange-900/30 dark:border-orange-800/30";
      case "neutral":
      default:
        return "bg-gray-950/20 dark:bg-gray-900/20 border-gray-900/30 dark:border-gray-800/30";
    }
  };

  return (
    <div className="mx-auto p-5 font-sans bg-black text-white min-h-screen">
      <h1 className="text-center text-xl lg:text-2xl font-bold text-yellow-200 mb-4">
        Welcome to BankWala
      </h1>
      <p className="text-sm lg:text-lg text-center text-gray-300 mb-8">
        Explore frequently asked questions tailored to your role in our spiritual community.
      </p>

      {Object.entries(faqData).map(([sectionTitle, items]) => (
        <div
          key={sectionTitle}
          className="mb-8 border border-gray-800 rounded-lg overflow-hidden bg-gray-900"
        >
          <h2 className="bg-gray-800 p-4 m-0 border-b border-gray-700 text-sm lg:text-lg font-semibold text-white">
            {sectionTitle}
          </h2>
          <div>
            {items.map((item, index) => (
              <div
                key={index}
                className={`border-b ${getSentimentClasses(item.sentiment)}`}
              >
                <button
                  onClick={() => toggleSection(sectionTitle, index)}
                  className="w-full p-4 border-none bg-transparent text-left cursor-pointer text-sm lg:text-lg font-bold text-white hover:text-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50"
                >
                  <span>Q: {item.question}</span>
                  <span className="float-right text-m lg:text-lg">
                    {openSections[`${sectionTitle}-${index}`] ? "−" : "+"}
                  </span>
                </button>
                {openSections[`${sectionTitle}-${index}`] && (
                  <div className="p-4 pt-0 first:pt-4">
                    <p className="m-0 text-gray-200 leading-relaxed text-sm lg:text-m">
                      A: {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BankWalaFAQHome;